import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import type { UpdateClientStatusResult } from '@epics/clientManagement/types/clientListType';
import { CLIENT_LABELS } from '@epics/clientManagement/constants/labels';
import { CLIENT_ERROR_MESSAGES } from '@epics/clientManagement/errors/errorMessages';
import clientEndpoints from './clientEndpoints';

const DEFAULT_ERROR = 'Wystąpił błąd podczas aktualizacji statusu klienta.';

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    return apiError.message ?? apiError.error ?? DEFAULT_ERROR;
  }
  return DEFAULT_ERROR;
};

export const updateClientStatusApiService = {
  updateClientStatus: async (params: {
    clientId: string;
    isActive: boolean;
  }): Promise<UpdateClientStatusResult> => {
    try {
      const result = await apiService.patchAPI(
        clientEndpoints.CLIENT_STATUS(params.clientId),
        { isActive: params.isActive },
        { skipGlobalErrorRedirect: true }
      );

      if (!result.error || result.status === 204) {
        return { success: true };
      }

      const statusCode = result.status;
      switch (statusCode) {
        case 403:
          return {
            success: false,
            error: CLIENT_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
          };
        case 404:
          return { success: false, error: 'Klient nie został znaleziony.' };
        case 409:
          return {
            success: false,
            error: params.isActive
              ? CLIENT_LABELS.STATUS_ERROR_ALREADY_ACTIVE
              : CLIENT_LABELS.STATUS_ERROR_ALREADY_INACTIVE,
          };
        case 400:
          return {
            success: false,
            error: 'Proszę podać prawidłowe dane wejściowe.',
          };
        default:
          if (result.isNetworkError) {
            return {
              success: false,
              error: 'Błąd połączenia. Sprawdź połączenie internetowe.',
            };
          }
          return { success: false, error: extractErrorMessage(result.data) };
      }
    } catch (error) {
      if (!(error instanceof AxiosError)) {
        return {
          success: false,
          error: error instanceof Error ? error.message : DEFAULT_ERROR,
        };
      }
      const statusCode = error.response?.status;
      switch (statusCode) {
        case 404:
          return { success: false, error: 'Klient nie został znaleziony.' };
        case 409:
          return {
            success: false,
            error: params.isActive
              ? CLIENT_LABELS.STATUS_ERROR_ALREADY_ACTIVE
              : CLIENT_LABELS.STATUS_ERROR_ALREADY_INACTIVE,
          };
        case 400:
          return {
            success: false,
            error: 'Proszę podać prawidłowe dane wejściowe.',
          };
        default:
          if (statusCode === undefined && error.request) {
            return {
              success: false,
              error: 'Błąd połączenia. Sprawdź połączenie internetowe.',
            };
          }
          return {
            success: false,
            error: extractErrorMessage(error.response?.data),
          };
      }
    }
  },
};
